/* WP-10 History (§14.5.4): monthly timeline of deposits/collections plus a
 * statement download sheet (PDF/XLSX). Cursor-paginated (§10.2 H3). */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { portalApi } from '../api/portal-api';
import type {
  HistoryResponse,
  MonthsResponse,
  PortalHistoryEntry,
  PortalStatement,
} from '../api/types';
import { SecondaryButton, TextLink } from '../components/primitives';
import { BottomSheet } from '../components/sheet';
import { HistoryTimelineEntry, MonthHeader } from '../components/timeline';
import { BackIcon, DownloadIcon, StatementIcon } from '../icons';
import { currentPortalLocale } from '../i18n';
import { formatMonthKey, hktDateKey } from '../utils/dates';
import { errorText } from '../utils/error-text';
import { historySentence } from '../utils/history-sentence';

export function HistoryPage() {
  const { t } = useTranslation();
  const locale = currentPortalLocale();
  const [items, setItems] = useState<PortalHistoryEntry[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [months, setMonths] = useState<string[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    portalApi
      .get<HistoryResponse>('/history')
      .then((res) => {
        setItems(res.items);
        setNextCursor(res.nextCursor ?? null);
      })
      .catch((err) => setError(errorText(err, t)));
    portalApi
      .get<MonthsResponse>('/history/months')
      .then((res) => setMonths(res.items))
      .catch(() => setMonths([]));
  }, [t]);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await portalApi.get<HistoryResponse>(
        `/history?cursor=${encodeURIComponent(nextCursor)}`,
      );
      setItems((prev) => [...(prev ?? []), ...res.items]);
      setNextCursor(res.nextCursor ?? null);
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setLoadingMore(false);
    }
  };

  const groups = useMemo(() => {
    const out = new Map<string, PortalHistoryEntry[]>();
    for (const e of items ?? []) {
      const key = hktDateKey(new Date(e.occurredAt)).slice(0, 7);
      if (!out.has(key)) out.set(key, []);
      out.get(key)!.push(e);
    }
    return [...out.entries()];
  }, [items]);

  return (
    <>
      <h1 className="hp-display-xl">{t('history.title')}</h1>

      {error ? (
        <p className="hp-danger-text" role="alert">
          {error}
        </p>
      ) : null}

      {months.length ? (
        <SecondaryButton
          icon={<StatementIcon size={18} />}
          onClick={() => setSheetOpen(true)}
          data-quality-id="portal.history.statements"
        >
          {t('history.download')}
        </SecondaryButton>
      ) : null}

      {groups.map(([month, entries]) => (
        <section key={month} className="hp-section">
          <MonthHeader monthKey={month} locale={locale} />
          <div className="hp-timeline">
            {entries.map((e) => (
              <HistoryTimelineEntry
                key={e.id}
                entry={e}
                sentence={historySentence(e, t)}
                locale={locale}
                secondary={
                  e.reference?.kind === 'collection' ? (
                    <Link className="hp-timeline__action" to={`/collect/${e.reference.id}`}>
                      {t('collect.title')} ›
                    </Link>
                  ) : undefined
                }
              />
            ))}
          </div>
        </section>
      ))}

      {items && items.length === 0 ? (
        <p className="hp-tertiary hp-section">{t('history.empty')}</p>
      ) : null}

      {nextCursor ? (
        <TextLink onClick={loadMore} chevron={false} data-quality-id="portal.history.more">
          {loadingMore ? t('common.loading') : t('common.loadMore')}
        </TextLink>
      ) : null}

      <BottomSheet open={sheetOpen} onClose={() => setSheetOpen(false)} title={t('history.downloadTitle')}>
        <StatementSheet months={months} onNavigate={() => setSheetOpen(false)} />
      </BottomSheet>
    </>
  );
}

function StatementSheet({
  months,
  onNavigate,
}: {
  months: string[];
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const locale = currentPortalLocale();
  const [month, setMonth] = useState<string | null>(null);
  const [format, setFormat] = useState<'pdf' | 'xlsx'>('pdf');

  const selected = month ?? months[0] ?? null;

  return (
    <div className="hp-stack">
      <div className="hp-scroll-x" role="radiogroup" aria-label={t('history.monthLabel')}>
        {months.slice(0, 12).map((m) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={selected === m}
            className={`hp-chip ${selected === m ? 'hp-chip--on' : ''}`}
            onClick={() => setMonth(m)}
            data-quality-id={`portal.statement.month.${m}`}
          >
            {formatMonthKey(m, locale)}
          </button>
        ))}
      </div>
      <div className="hp-row" role="radiogroup" aria-label={t('history.monthLabel')}>
        {(['pdf', 'xlsx'] as const).map((f) => (
          <button
            key={f}
            type="button"
            role="radio"
            aria-checked={format === f}
            className={`hp-chip ${format === f ? 'hp-chip--on' : ''}`}
            onClick={() => setFormat(f)}
            data-quality-id={`portal.statement.format.${f}`}
          >
            {f === 'pdf' ? t('history.pdf') : t('history.excel')}
          </button>
        ))}
      </div>
      {selected ? (
        <>
          <a
            className="hp-btn hp-btn--primary"
            href={portalApi.url(`/history/statement/${selected}?format=${format}`)}
            data-quality-id="portal.statement.download"
          >
            <DownloadIcon size={18} /> {t('history.download')}
          </a>
          <Link
            to={`/history/statement/${selected}`}
            className="hp-textlink"
            onClick={onNavigate}
          >
            {t('history.viewStatement')}
          </Link>
        </>
      ) : (
        <p className="hp-tertiary">{t('history.empty')}</p>
      )}
    </div>
  );
}

/* Statement month view (§14.5.4 /history/statement/:month): per-product
 * Opening / In / Out / Closing ledger from GET /statements/:month plus the
 * PDF/XLSX export actions (§11.2). */
export function StatementMonthPage() {
  const { t } = useTranslation();
  const locale = currentPortalLocale();
  const { month = '' } = useParams<{ month: string }>();
  const navigate = useNavigate();
  const [stmt, setStmt] = useState<PortalStatement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!/^\d{4}-\d{2}$/.test(month)) {
      setError(t('errors.PORTAL_VALIDATION_FAILED'));
      return;
    }
    portalApi
      .get<PortalStatement>(`/statements/${month}`)
      .then(setStmt)
      .catch((err) => setError(errorText(err, t)));
  }, [month, t]);

  return (
    <div className="hp-detail-narrow">
      <div className="hp-row hp-detail-head">
        <button
          type="button"
          className="hp-textlink"
          onClick={() => navigate('/history')}
          aria-label={t('common.back')}
        >
          <BackIcon size={18} />
        </button>
        <div>
          <p className="hp-label">{stmt?.memberCodeMasked}</p>
          <h1 className="hp-display-xl">{formatMonthKey(month, locale)}</h1>
        </div>
      </div>

      {error ? (
        <p className="hp-danger-text" role="alert">
          {error}
        </p>
      ) : null}

      {stmt ? (
        <div className="hp-statement" role="table" aria-label={formatMonthKey(month, locale)}>
          <div className="hp-statement__row hp-statement__row--head" role="row">
            <span role="columnheader">{t('history.cols.product')}</span>
            <span role="columnheader" className="hp-num">
              {t('history.cols.opening')}
            </span>
            <span role="columnheader" className="hp-num">
              {t('history.cols.in')}
            </span>
            <span role="columnheader" className="hp-num">
              {t('history.cols.out')}
            </span>
            <span role="columnheader" className="hp-num">
              {t('history.cols.closing')}
            </span>
          </div>
          {stmt.rows.map((r) => (
            <div key={r.productId} className="hp-statement__row" role="row">
              <span role="cell">
                {r.product}
                <span className="hp-listrow__sub">{r.brand}</span>
              </span>
              <span role="cell" className="hp-num">
                {r.opening}
              </span>
              <span role="cell" className="hp-num">
                {r.inQty}
              </span>
              <span role="cell" className="hp-num">
                {r.outQty}
              </span>
              <span role="cell" className="hp-num">
                {r.closing}
              </span>
            </div>
          ))}
          {stmt.rows.length === 0 ? (
            <p className="hp-tertiary">{t('history.empty')}</p>
          ) : null}
        </div>
      ) : !error ? (
        <div className="hp-skel hp-detail-skel" />
      ) : null}

      <div className="hp-row hp-detail-head">
        <a
          className="hp-btn hp-btn--primary"
          href={portalApi.url(`/history/statement/${month}?format=pdf`)}
          data-quality-id="portal.statement.pdf"
        >
          <DownloadIcon size={18} /> {t('history.pdf')}
        </a>
        <a
          className="hp-btn hp-btn--quiet"
          href={portalApi.url(`/history/statement/${month}?format=xlsx`)}
          data-quality-id="portal.statement.xlsx"
        >
          <DownloadIcon size={18} /> {t('history.excel')}
        </a>
      </div>
    </div>
  );
}
